import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	NgZone,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { BREAKPOINTS } from '@go-modules/common/interfaces/breakpoints';
import { BehaviorSubject, Subject, pairwise, startWith, takeUntil, filter } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { MatSidenav } from '@angular/material/sidenav';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { PubnubService, pubnubToken } from 'go-modules/go-pubnub/pubnub.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerSurveySnackBarComponent } from '@go-modules/components/snack-bars/customer-survey/customer-survey-snack-bar.component';
import { CookieService } from 'ngx-cookie-service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { BreadCrumbStateService } from '@go-modules/services/breadcrumb-state/breadcrumb-state.service';
import { SelectedInterface, SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import * as dayjs from 'dayjs';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import { GoTourService, goTourServiceToken } from 'go-modules/go-tour/go-tour.service';
import { DraftTourSnackBarComponent } from '@go-modules/components/snack-bars/draft-tour/draft-tour-snack-bar.component';
import { CourseTrialService } from '@go-modules/services/course-trial/course-trial.service';
import { LicenseTrialsService } from '@go-modules/services/license-trials/license-trials.service';

export const DRAFT_TOUR_KEY = 'draft-tour';
export const DRAFT_TOUR_CONSTRAINTS = {
	isOnboarding: false,
	tourStartDate: dayjs('2024-08-01 09:00:00').toDate()
};

@Component({
	selector: 'dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('slideInOut', [
			state('collapsed', style({ width: '{{lowWidth}}' }), { params: { lowWidth: '77px' } }),
			state('expanded', style({
				width: '216px'
			})),
			transition('collapsed => expanded', animate('250ms ease-in-out')),
			transition('expanded => collapsed', animate('250ms ease-in-out'))
		]),
		trigger('slideInOutContent', [
			state('collapsed', style({
				marginLeft: '77px'
			})),
			state('expanded', style({
				marginLeft: '216px'
			})),
			transition('collapsed => expanded', animate('250ms ease-in-out')),
			transition('expanded => collapsed', animate('250ms ease-in-out'))
		])
	],
	standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
	@ViewChild('mainSideNav')
	public mainSideNav: MatSidenav;
	public collapsed: boolean = false;
	public collapsed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.collapsed);
	public isMobile: boolean = false;
	public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public opened: boolean = true;
	public states = ROUTE_NAMES;
	public environmentVarsService: EnvironmentVarsService;
	public isLTI: boolean = false;
	public showMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showSideNav$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	// Luck shouldn't be public, but you literally CANNOT test this in
	// a deterministic way without checking what luck is at.
	public luck = 0;

	private previousOrgId: number;
	private componentDestroyed$$ = new Subject();

	constructor (
		private routerService: RouterService,
		private breakpointObserver: BreakpointObserver,
		private snackbar: MatSnackBar,
		private cookieService: CookieService,
		private featureFlagService: NgxFeatureFlagService,
		private breadCrumbStateService: BreadCrumbStateService,
		private ngZone: NgZone,
		private licenseTrialsService: LicenseTrialsService,
		private courseTrialService: CourseTrialService,
		@Inject(pubnubToken) private pubnub: PubnubService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(masqueradeToken) private masquerade: Masquerade,
		@Inject(goTourServiceToken) private goTour: GoTourService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnDestroy (): void {
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public ngOnInit () {
		this.isLTI = this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);

		this.breadCrumbStateService.navState$
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe(() => {
				this.updateShowSideNav();
			});

		// Initialize showSideNav$ on component load
		this.updateShowSideNav();

		this.breadCrumbStateService.navState$
			.pipe(
				takeUntil(this.componentDestroyed$$),
				startWith(undefined),
				pairwise()
			).subscribe((navStates) => {
				const license = this.selectedService.getLicense();

				if (navStates[0] === ROUTE_NAMES.DASHBOARD_SESSION_VIEW &&
					navStates[1] !== ROUTE_NAMES.DASHBOARD_SESSION_VIEW &&
					this.featureFlagService.isAvailable('CUSTOMER_SURVEY') &&
					CustomerSurveySnackBarComponent.canShow(this.userService.currentUser.user_id) &&
					!this.cookieService.get('e2e-test-run') &&
					!this.masquerade.isMasked() &&
					!this.environmentVarsService.get(EnvironmentVarsService.VAR.WHITELABEL) &&
					(!license || !license.salesforce_license.is_free_trial)
				) {
					const randomNumber = Math.random() * 10;
					const percentage = 1; // 10%
					const probability = percentage + this.luck;

					if (randomNumber > probability) {
						// increment luck to increase probability
						this.luck += 0.5;
						return;
					}

					CustomerSurveySnackBarComponent.open(this.snackbar);
				}

				if (!this.cookieService.get('e2e-test-run') &&
					(navStates[1] === ROUTE_NAMES.DASHBOARD_FOLDER_VIEW ||
						navStates[1] === ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW)
				) {
					this.checkToShowDraftTourSnackbar();
				}
			});

		this.breakpointObserver
			.observe([`(max-width: ${BREAKPOINTS.SCREEN_MD})`])
			.subscribe((result: BreakpointState) => {
				if (result.matches) {
					this.opened = false;
					this.collapsed = false;
					this.collapsed$.next(false);
					this.isMobile = true;
					this.isMobile$.next(true);
				} else {
					this.opened = true;
					this.isMobile = false;
					this.isMobile$.next(false);
				}
			});

		if (this.isLTI) {
			this.pubnub.updateChannels();
		}

		if (this.userService.currentUser.isInstructor && this.featureFlagService.isAvailable('AI_TRIAL')) {
			this.selectedService.selectedSubject
				.asObservable()
				.pipe(
					takeUntil(this.componentDestroyed$$),
					filter((selected: SelectedInterface) => {
						return selected.org && selected.org.group_id !== this.previousOrgId;
					}))
				.subscribe((selected: SelectedInterface) => {
					const { group_id } = selected.org;
					this.previousOrgId = group_id;

					if (this.courseTrialService.licenseTrialBlocked(group_id)) {
						this.showMessage$.next(false);
					} else {
						this.licenseTrialsService.checkAllowed(group_id)
							.subscribe((response) => {
								if (!response.allowed) {
									this.courseTrialService.blockLicenseTrial(group_id);
								}

								this.showMessage$.next(response.allowed);
							});
					}
				});
		}
	}

	private updateShowSideNav () {
		const shouldShow = !(
			this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_SESSION_VIEW) ||
			this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_WIZARD)
		);
		this.showSideNav$.next(shouldShow);
	}

	public toggleCollapse () {
		this.collapsed = !this.collapsed;
		this.collapsed$.next(this.collapsed);
	}

	public checkToShowDraftTourSnackbar () {
		this.goTour.canTourBeViewed(
			DRAFT_TOUR_KEY,
			DRAFT_TOUR_CONSTRAINTS
		).then(() => {
			this.ngZone.run(() => {
				DraftTourSnackBarComponent.open(this.snackbar).afterDismissed().subscribe(() => {
					this.goTour.markTourViewed(DRAFT_TOUR_KEY);
				});
			});
		}).catch(() => { });
	}
}
